import React from 'react';

interface ProcessingStage {
  id: string;
  label: string;
  icon: string;
  duration: number; // milliseconds
}

interface ProcessingOverlayProps {
  isProcessing: boolean;
  currentStage: number;
  progress: number;
  filesProcessed?: number;
  totalFiles?: number;
}

const PROCESSING_STAGES: ProcessingStage[] = [
  { id: 'upload', label: 'Uploading documents', icon: '📤', duration: 1000 },
  { id: 'scan', label: 'Reading PDF content', icon: '📄', duration: 2000 },
  { id: 'redact', label: 'Redacting personal information', icon: '🔒', duration: 3000 },
  { id: 'extract', label: 'Extracting lab values', icon: '🧪', duration: 4000 },
  { id: 'analyze', label: 'Analyzing results', icon: '📊', duration: 3000 },
  { id: 'validate', label: 'Validating accuracy', icon: '✅', duration: 2000 },
  { id: 'complete', label: 'Processing complete!', icon: '🎉', duration: 1000 },
];

const ProcessingOverlay: React.FC<ProcessingOverlayProps> = ({ 
  isProcessing, 
  currentStage, 
  progress,
  filesProcessed = 0,
  totalFiles = 1
}) => {
  if (!isProcessing) return null;

  const currentStageData = PROCESSING_STAGES[currentStage] || PROCESSING_STAGES[0];
  
  return (
    <div className="processing-overlay">
      <div className="processing-container">
        <div className="processing-content">
          <div className="processing-icon-container">
            <div className="processing-icon">{currentStageData.icon}</div>
            <div className="processing-spinner"></div>
          </div>
          
          <h3 className="processing-title">{currentStageData.label}</h3>
          
          {totalFiles > 1 && (
            <p className="text-sm text-gray-600 mt-2">
              Processing file {filesProcessed + 1} of {totalFiles}
            </p>
          )}
          
          {(currentStage === 3 || currentStage === 4) && (
            <p className="text-xs text-gray-500 mt-2">
              This may take a moment as we analyze your lab data...
            </p>
          )}
          
          <div className="processing-progress-container">
            <div className="processing-progress-bar">
              <div 
                className="processing-progress-fill" 
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="processing-progress-text">{Math.round(progress)}%</span>
          </div>
          
          <div className="processing-stages">
            {PROCESSING_STAGES.map((stage, index) => (
              <div 
                key={stage.id} 
                className={`processing-stage ${
                  index < currentStage ? 'completed' : 
                  index === currentStage ? 'active' : ''
                }`}
              >
                <div className="stage-dot"></div>
                <span className="stage-label">{stage.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProcessingOverlay;